import { useId, useState } from 'react';
import { useRuntimeThemeStore } from '@core/store/runtimeThemeStore';
import { useIndustry } from '@/app/providers/industry-context.js';
import { useCopy } from '@/app/providers/useCopy';

const CUSTOM_LOADER_KEYFRAMES = `
@keyframes custom-loader-pulse {
  0%, 100% { transform: scale(0.92); opacity: 0.75; }
  50% { transform: scale(1.04); opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .loading-state__icon--custom { animation: none !important; }
}`;

export default function LoadingState({ minHeight = 240, label, size = 'large', className = '' }) {
  const { key } = useIndustry();
  const t = useCopy();
  const gradientId = useId();
  // Merchant loader image (theme_settings.loader_url) replaces the built-in
  // animation; a broken image falls back to it.
  const loaderUrl = useRuntimeThemeStore((s) => s.loaderUrl);
  const [failedUrl, setFailedUrl] = useState('');
  const customUrl = loaderUrl && loaderUrl !== failedUrl ? loaderUrl : '';
  // GIFs animate themselves; any other image gets a gentle pulse.
  const isGif = /\.gif(\?|#|$)|^data:image\/gif/i.test(customUrl);
  const loader = loaderByIndustry[key] ?? loaderByIndustry.default;
  // A loader may carry its own caption ([copy key, fallback]); an explicit
  // `label` prop still wins.
  const caption = label ?? (loader.label ? t(...loader.label) : null);
  const icon = typeof loader.icon === 'function' ? loader.icon(gradientId) : loader.icon;
  const loaderSize =
    size === 'small'
      ? '4rem'
      : size === 'medium'
        ? '5rem'
        : size === 'xlarge'
          ? '8.5rem'
          : '6.5rem';

  return (
    <div
      role="status"
      aria-busy="true"
      className={`loading-state ${className}`.trim()}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight,
        color: 'var(--color-text-secondary)',
      }}
    >
      {customUrl ? (
        <>
          {isGif ? null : <style>{CUSTOM_LOADER_KEYFRAMES}</style>}
          <img
            src={customUrl}
            alt=""
            aria-hidden="true"
            className="loading-state__icon loading-state__icon--custom"
            style={{
              width: loaderSize,
              height: loaderSize,
              objectFit: 'contain',
              animation: isGif ? undefined : 'custom-loader-pulse 1.4s ease-in-out infinite',
            }}
            onError={() => setFailedUrl(loaderUrl)}
          />
        </>
      ) : (
        <>
          <style>{loader.keyframes}</style>
          <span
            aria-hidden="true"
            className="loading-state__icon"
            style={{
              // A loader may be wider than tall (`aspect`, width ÷ height).
              width: loader.aspect ? 'auto' : loaderSize,
              height: loaderSize,
              aspectRatio: loader.aspect,
              display: 'grid',
              placeItems: 'center',
              color: loader.color,
            }}
          >
            {icon}
          </span>
        </>
      )}
      {caption ? <span className="loading-state__label">{caption}</span> : null}
    </div>
  );
}

// One walking veggie for the grocery parade: shadow, back leg, a bobbing
// torso (back arm, the veggie body passed as children, face, front arm) and
// the front leg, all centred on `x`. Hips sit at y 79, shoulders at y 58;
// `delay` (seconds, negative) offsets its step from the others.
function VeggieWalker({ x, delay, legColor, shoe, eyeY, children }) {
  const cycle = (name) => `${name} 0.9s ease-in-out ${delay}s infinite`;
  // The far-side sneaker is a shade darker so the two feet read apart.
  const sneaker = (footX, isBack) => (
    <>
      <path
        d={`M${footX - 2} 91.6 Q${footX - 2} 87 ${footX + 3} 87 Q${footX + 8} 87 ${footX + 10} 90.2 L${footX + 10} 91.6 Z`}
        style={{ fill: isBack ? `color-mix(in srgb, ${shoe} 80%, #000)` : shoe }}
      />
      <rect
        x={footX - 2.6}
        y={91.2}
        width={13.2}
        height={2.3}
        rx={1.15}
        fill={isBack ? '#dfe3e7' : '#fff'}
      />
      <path
        d={`M${footX + 1.5} 88.6 l2.4 1.4 M${footX + 3.6} 88.2 l2.4 1.4`}
        stroke="#fff"
        strokeWidth={0.8}
        strokeLinecap="round"
      />
    </>
  );
  const glove = (hx, hy) => (
    <>
      <circle cx={hx} cy={hy} r={3.3} fill="#fff" stroke="#cfd6dc" strokeWidth={0.7} />
      <ellipse
        cx={hx + 2.6}
        cy={hy - 1.4}
        rx={1.3}
        ry={1.8}
        fill="#fff"
        stroke="#cfd6dc"
        strokeWidth={0.6}
      />
      <path
        d={`M${hx - 1.6} ${hy + 1.2} h3`}
        stroke="#d9dee3"
        strokeWidth={0.6}
        strokeLinecap="round"
      />
    </>
  );

  return (
    <g>
      <ellipse
        cx={x}
        cy={95}
        rx={13}
        ry={2.4}
        fill="#000"
        style={{ transformOrigin: `${x}px 95px`, animation: cycle('gxVegShadow') }}
      />
      <g style={{ transformOrigin: `${x + 2}px 79px`, animation: cycle('gxVegLegBack') }}>
        <path
          d={`M${x + 2} 79 L${x + 3} 88`}
          stroke={legColor}
          strokeWidth={2.8}
          strokeLinecap="round"
        />
        {sneaker(x + 1, true)}
      </g>
      <g style={{ animation: cycle('gxVegBob') }}>
        <g style={{ transformOrigin: `${x + 3}px 58px`, animation: cycle('gxVegArmBack') }}>
          <path
            d={`M${x + 3} 58 Q${x + 8} 63 ${x + 7} 69`}
            fill="none"
            stroke={legColor}
            strokeWidth={2.4}
            strokeLinecap="round"
          />
          {glove(x + 7, 70.5)}
        </g>
        {children}
        {/* face, turned towards the walking direction */}
        <ellipse cx={x + 1.5} cy={eyeY} rx={2.5} ry={3} fill="#fff" />
        <ellipse cx={x + 7} cy={eyeY} rx={2.3} ry={2.8} fill="#fff" />
        <circle cx={x + 2.4} cy={eyeY + 0.4} r={1.3} fill="#1f2a1c" />
        <circle cx={x + 7.8} cy={eyeY + 0.4} r={1.2} fill="#1f2a1c" />
        <circle cx={x + 2.9} cy={eyeY - 0.2} r={0.45} fill="#fff" />
        <circle cx={x + 8.3} cy={eyeY - 0.2} r={0.45} fill="#fff" />
        <path
          d={`M${x - 0.6} ${eyeY - 4.2} Q${x + 1.5} ${eyeY - 5.4} ${x + 3.4} ${eyeY - 4.4} M${x + 5.4} ${eyeY - 4.4} Q${x + 7.2} ${eyeY - 5.4} ${x + 9} ${eyeY - 4.2}`}
          fill="none"
          stroke="#1f2a1c"
          strokeWidth={0.8}
          strokeLinecap="round"
        />
        <ellipse cx={x + 9.5} cy={eyeY + 5} rx={1.8} ry={1.1} fill="#ff8a80" opacity={0.55} />
        <path
          d={`M${x + 2.5} ${eyeY + 6} Q${x + 5} ${eyeY + 8.4} ${x + 7.5} ${eyeY + 6}`}
          fill="none"
          stroke="#1f2a1c"
          strokeWidth={1.1}
          strokeLinecap="round"
        />
        <g style={{ transformOrigin: `${x - 3}px 58px`, animation: cycle('gxVegArmFront') }}>
          <path
            d={`M${x - 3} 58 Q${x - 8} 63 ${x - 7} 69`}
            fill="none"
            stroke={legColor}
            strokeWidth={2.4}
            strokeLinecap="round"
          />
          {glove(x - 7, 70.5)}
        </g>
      </g>
      <g style={{ transformOrigin: `${x - 2}px 79px`, animation: cycle('gxVegLegFront') }}>
        <path
          d={`M${x - 2} 79 L${x - 3} 88`}
          stroke={legColor}
          strokeWidth={2.8}
          strokeLinecap="round"
        />
        {sneaker(x - 4, false)}
      </g>
    </g>
  );
}

const loaderByIndustry = {
  // Grocery: a parade of veggie mascots — cucumber, eggplant, green bean and
  // carrot — walking side by side in white gloves and chunky sneakers. Each
  // bobs with arms and legs swinging in opposition; steps are staggered so
  // they don't march in lockstep. Glossy gradients give the 3D look.
  grocery: {
    color: 'var(--color-primary, #347758)',
    aspect: 2,
    keyframes: `
      @keyframes gxVegBob {
        0%, 50%, 100% { transform: translateY(0); }
        25%, 75% { transform: translateY(-2px); }
      }
      @keyframes gxVegLegFront {
        0%, 100% { transform: rotate(24deg); }
        50% { transform: rotate(-24deg); }
      }
      @keyframes gxVegLegBack {
        0%, 100% { transform: rotate(-24deg); }
        50% { transform: rotate(24deg); }
      }
      @keyframes gxVegArmFront {
        0%, 100% { transform: rotate(-30deg); }
        50% { transform: rotate(26deg); }
      }
      @keyframes gxVegArmBack {
        0%, 100% { transform: rotate(26deg); }
        50% { transform: rotate(-30deg); }
      }
      @keyframes gxVegShadow {
        0%, 50%, 100% { transform: scaleX(1); opacity: 0.16; }
        25%, 75% { transform: scaleX(0.85); opacity: 0.1; }
      }
      @media (prefers-reduced-motion: reduce) {
        .gx-veggies * { animation: none !important; }
      }
    `,
    icon: (id) => (
      <svg
        className="gx-veggies"
        viewBox="0 0 200 100"
        style={{ width: '100%', height: '100%', display: 'block', overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={`${id}-cuke`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#2f7d2b" />
            <stop offset="0.4" stopColor="#5fb847" />
            <stop offset="1" stopColor="#25661f" />
          </linearGradient>
          <radialGradient id={`${id}-egg`} cx="0.35" cy="0.35" r="0.8">
            <stop offset="0" stopColor="#b77be6" />
            <stop offset="0.55" stopColor="#7a35b8" />
            <stop offset="1" stopColor="#4a1a7a" />
          </radialGradient>
          <linearGradient id={`${id}-bean`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#5ea83a" />
            <stop offset="0.45" stopColor="#9ad65f" />
            <stop offset="1" stopColor="#4c9230" />
          </linearGradient>
          <linearGradient id={`${id}-carrot`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#e8741c" />
            <stop offset="0.45" stopColor="#ffa448" />
            <stop offset="1" stopColor="#d9610f" />
          </linearGradient>
          <linearGradient id={`${id}-leaf`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#7cc447" />
            <stop offset="1" stopColor="#3f8a24" />
          </linearGradient>
        </defs>

        <VeggieWalker x={26} delay={0} legColor="#2f7d2b" shoe="#f5c518" eyeY={42}>
          {/* cucumber: tall capsule with bumps and a gloss */}
          <rect x={17} y={22} width={18} height={58} rx={9} fill={`url(#${id}-cuke)`} />
          {[
            [21, 34],
            [29, 30],
            [24, 52],
            [31, 60],
            [21, 70],
            [28, 74],
          ].map(([cx, cy]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={1.1} fill="#8fd46c" opacity={0.8} />
          ))}
          <path
            d="M20.5 30 Q19.5 50 20.5 72"
            fill="none"
            stroke="#fff"
            strokeWidth={2}
            strokeLinecap="round"
            opacity={0.35}
          />
          <ellipse cx={26} cy={22.5} rx={4} ry={1.6} fill="#9bdc75" />
        </VeggieWalker>

        <VeggieWalker x={74} delay={-0.22} legColor="#5a2490" shoe="#5cb83a" eyeY={52}>
          {/* eggplant: teardrop body under a green cap and stem */}
          <path
            d="M69 34 Q61 52 62 66 Q63 81 74 81 Q86 81 86 66 Q87 52 79 34 Z"
            fill={`url(#${id}-egg)`}
          />
          <path
            d="M67 46 Q64.5 58 66 70"
            fill="none"
            stroke="#fff"
            strokeWidth={2}
            strokeLinecap="round"
            opacity={0.35}
          />
          <path
            d="M66 37 Q70 30 74 31 Q78 30 82 37 Q78.5 35 77 39 Q75 35 74 39 Q72.5 35 71 39 Q69.5 35 66 37 Z"
            fill={`url(#${id}-leaf)`}
          />
          <path
            d="M74 31 Q74 25 77.5 22"
            fill="none"
            stroke="#3f8a24"
            strokeWidth={2.4}
            strokeLinecap="round"
          />
        </VeggieWalker>

        <VeggieWalker x={124} delay={-0.45} legColor="#4c9230" shoe="#f08a24" eyeY={40}>
          {/* green bean: slim pod with a seam and a curled tip */}
          <path
            d="M119 26 Q116 52 117 79 Q124 83 131 79 Q132 52 129 26 Q124 20 119 26 Z"
            fill={`url(#${id}-bean)`}
          />
          <path
            d="M128 30 Q129.5 54 128.5 76"
            fill="none"
            stroke="#3f7d25"
            strokeWidth={0.9}
            opacity={0.6}
          />
          <path
            d="M120.5 32 Q119.5 52 120.5 72"
            fill="none"
            stroke="#fff"
            strokeWidth={1.6}
            strokeLinecap="round"
            opacity={0.4}
          />
          <path
            d="M124 22 Q123 15 127.5 13"
            fill="none"
            stroke="#5ea83a"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </VeggieWalker>

        <VeggieWalker x={172} delay={-0.67} legColor="#d9610f" shoe="#5cb83a" eyeY={46}>
          {/* carrot: tapered root with ridges and a leafy top */}
          <path
            d="M178 32 Q172 28 166 32 L162.5 40 M172 31 L172 18 M172 31 L178.5 20 M172 31 L166 21"
            fill="none"
            stroke="#4d9a2f"
            strokeWidth={2.4}
            strokeLinecap="round"
          />
          <ellipse
            cx={165.5}
            cy={20}
            rx={3}
            ry={5}
            fill={`url(#${id}-leaf)`}
            transform="rotate(-25 165.5 20)"
          />
          <ellipse cx={172} cy={16} rx={3} ry={5.5} fill={`url(#${id}-leaf)`} />
          <ellipse
            cx={179}
            cy={19}
            rx={3}
            ry={5}
            fill={`url(#${id}-leaf)`}
            transform="rotate(25 179 19)"
          />
          <path
            d="M161 34 Q160 60 167 80 Q172 83 177 80 Q184 60 183 34 Q172 29 161 34 Z"
            fill={`url(#${id}-carrot)`}
          />
          <path
            d="M163 44 h4 M178 50 h3.5 M164 60 h3.5 M177.5 66 h3 M166 73 h3"
            stroke="#c4560c"
            strokeWidth={1}
            strokeLinecap="round"
            opacity={0.7}
          />
          <path
            d="M165 38 Q164 56 168 72"
            fill="none"
            stroke="#fff"
            strokeWidth={1.8}
            strokeLinecap="round"
            opacity={0.35}
          />
        </VeggieWalker>
      </svg>
    ),
  },
  fish: {
    color: '#0284c7',
    keyframes: `
      @keyframes fishRingSpin {
        to { transform: rotate(360deg); }
      }
      @keyframes fishRingSpinReverse {
        to { transform: rotate(-360deg); }
      }
      @keyframes fishGlowPulse {
        0%, 100% { opacity: 0.35; transform: scale(0.94); }
        50% { opacity: 0.7; transform: scale(1.05); }
      }
      @keyframes fishSwim {
        0%, 100% { transform: translateX(-0.2rem) rotate(-3deg); }
        50% { transform: translateX(0.3rem) rotate(3deg); }
      }
      @keyframes fishTailFlick {
        0%, 100% { transform: rotate(0deg); }
        50% { transform: rotate(-14deg); }
      }
      @keyframes fishBubbleRise {
        0% { transform: translateY(0) scale(0.6); opacity: 0; }
        35% { opacity: 0.9; }
        100% { transform: translateY(-1.3rem) scale(1); opacity: 0; }
      }
    `,
    icon: (gradientId) => (
      <svg viewBox="0 0 72 72" style={{ width: '100%', height: '100%', display: 'block' }}>
        <defs>
          <radialGradient id={`${gradientId}-glow`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0" />
          </radialGradient>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7dd3fc" />
            <stop offset="50%" stopColor="#0ea5e9" />
            <stop offset="100%" stopColor="#075985" />
          </linearGradient>
          <linearGradient id={`${gradientId}-ring`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0" />
            <stop offset="100%" stopColor="#0284c7" />
          </linearGradient>
        </defs>

        <circle
          cx="36"
          cy="36"
          r="30"
          fill={`url(#${gradientId}-glow)`}
          style={{
            transformOrigin: '36px 36px',
            animation: 'fishGlowPulse 1.6s ease-in-out infinite',
          }}
        />

        <circle
          cx="36"
          cy="36"
          r="27"
          fill="none"
          stroke="var(--color-border, #e5e7eb)"
          strokeWidth="2.5"
          opacity="0.3"
        />

        <circle
          cx="36"
          cy="36"
          r="27"
          fill="none"
          stroke={`url(#${gradientId}-ring)`}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray="56 170"
          style={{
            transformOrigin: '36px 36px',
            animation: 'fishRingSpin 1.3s linear infinite',
          }}
        />
        <circle
          cx="36"
          cy="36"
          r="22"
          fill="none"
          stroke={`url(#${gradientId}-ring)`}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray="16 118"
          opacity="0.5"
          style={{
            transformOrigin: '36px 36px',
            animation: 'fishRingSpinReverse 1.9s linear infinite',
          }}
        />

        <g
          style={{ transformOrigin: '36px 38px', animation: 'fishSwim 950ms ease-in-out infinite' }}
        >
          <path
            d="M20 38c8.5-10.5 24-13 36 0-12 13-27.5 10.5-36 0z"
            fill={`url(#${gradientId})`}
            filter="drop-shadow(0 2px 3px rgba(7, 89, 133, 0.35))"
          />
          <path
            d="M20 38l-8-8.5v17z"
            fill="#075985"
            style={{
              transformOrigin: '18px 38px',
              animation: 'fishTailFlick 700ms ease-in-out infinite',
            }}
          />
          <circle cx="47" cy="35" r="2.4" fill="#ffffff" />
          <circle cx="47.8" cy="35" r="1.1" fill="#0c4a6e" />
          <path
            d="M31 29c-3 4.5-3 12.5 0 18"
            fill="none"
            stroke="#e0f2fe"
            strokeWidth="1.6"
            strokeLinecap="round"
            opacity="0.8"
          />
        </g>

        <circle
          cx="13"
          cy="20"
          r="2.6"
          fill="#7dd3fc"
          style={{ animation: 'fishBubbleRise 1100ms ease-in-out infinite' }}
        />
        <circle
          cx="22"
          cy="15"
          r="1.8"
          fill="#bae6fd"
          style={{ animation: 'fishBubbleRise 1100ms ease-in-out 220ms infinite' }}
        />
        <circle
          cx="16"
          cy="12"
          r="1.3"
          fill="#e0f2fe"
          style={{ animation: 'fishBubbleRise 1100ms ease-in-out 420ms infinite' }}
        />
      </svg>
    ),
  },
  // Butcher's block: a cleaver chops three slices off the slab in turn,
  // stepping left each time; the slab shortens and every slice tips over onto
  // the board beside the last. Then it all resets. One 2.7s loop.
  meat: {
    color: '#b91c1c',
    label: ['freshCutting', 'Fresh cutting…'],
    aspect: '120 / 104',
    keyframes: `
      @keyframes meatChop {
        0% { transform: translate(0px, -24px) rotate(-16deg); }
        10% { transform: translate(-5px, -32px) rotate(-24deg); }
        14% { transform: translate(-5px, 0) rotate(0deg); }
        18% { transform: translate(-5px, -2px) rotate(-1deg); }
        26% { transform: translate(-5px, -24px) rotate(-16deg); }
        36% { transform: translate(-14px, -32px) rotate(-24deg); }
        40% { transform: translate(-14px, 0) rotate(0deg); }
        44% { transform: translate(-14px, -2px) rotate(-1deg); }
        52% { transform: translate(-14px, -24px) rotate(-16deg); }
        62% { transform: translate(-23px, -32px) rotate(-24deg); }
        66% { transform: translate(-23px, 0) rotate(0deg); }
        70% { transform: translate(-23px, -2px) rotate(-1deg); }
        78% { transform: translate(-23px, -24px) rotate(-16deg); }
        90% { transform: translate(-23px, -24px) rotate(-16deg); }
        100% { transform: translate(0px, -24px) rotate(-16deg); }
      }
      @keyframes meatSlab {
        0%, 13% { transform: scaleX(1); opacity: 1; }
        14%, 39% { transform: scaleX(0.85); opacity: 1; }
        40%, 65% { transform: scaleX(0.7); opacity: 1; }
        66%, 90% { transform: scaleX(0.55); opacity: 1; }
        94% { transform: scaleX(0.55); opacity: 0; }
        96% { transform: scaleX(1); opacity: 0; }
        100% { transform: scaleX(1); opacity: 1; }
      }
      @keyframes meatSlice0 {
        0%, 13% { opacity: 0; transform: translateX(0) rotate(0deg); }
        15% { opacity: 1; transform: translateX(3px) rotate(0deg); }
        24%, 90% { opacity: 1; transform: translateX(26px) rotate(10deg); }
        94%, 100% { opacity: 0; transform: translateX(26px) rotate(10deg); }
      }
      @keyframes meatSlice1 {
        0%, 39% { opacity: 0; transform: translateX(0) rotate(0deg); }
        41% { opacity: 1; transform: translateX(3px) rotate(0deg); }
        50%, 90% { opacity: 1; transform: translateX(26px) rotate(10deg); }
        94%, 100% { opacity: 0; transform: translateX(26px) rotate(10deg); }
      }
      @keyframes meatSlice2 {
        0%, 65% { opacity: 0; transform: translateX(0) rotate(0deg); }
        67% { opacity: 1; transform: translateX(3px) rotate(0deg); }
        76%, 90% { opacity: 1; transform: translateX(26px) rotate(10deg); }
        94%, 100% { opacity: 0; transform: translateX(26px) rotate(10deg); }
      }
      @keyframes meatImpact {
        0%, 13% { opacity: 0; transform: translateX(0) scale(0.5); }
        14% { opacity: 1; transform: translateX(-5px) scale(1); }
        22%, 39% { opacity: 0; transform: translateX(-5px) scale(1.3); }
        40% { opacity: 1; transform: translateX(-14px) scale(1); }
        48%, 65% { opacity: 0; transform: translateX(-14px) scale(1.3); }
        66% { opacity: 1; transform: translateX(-23px) scale(1); }
        74%, 100% { opacity: 0; transform: translateX(-23px) scale(1.3); }
      }
      @keyframes meatBoard {
        0%, 100% { transform: translateY(0); }
        14% { transform: translateY(1px); }
        18% { transform: translateY(0); }
        40% { transform: translateY(1px); }
        44% { transform: translateY(0); }
        66% { transform: translateY(1px); }
        70% { transform: translateY(0); }
      }
    `,
    icon: (
      <svg viewBox="0 -14 120 104" width="100%" height="100%" overflow="visible">
        {/* Shadow */}
        <ellipse cx="60" cy="87" rx="50" ry="3" fill="#000" opacity="0.12" />

        <g style={{ animation: 'meatBoard 2700ms ease-in-out infinite' }}>
          {/* Chopping board */}
          <rect x="6" y="73" width="108" height="12" rx="6" fill="#9a5b2a" />
          <rect x="6" y="73" width="108" height="9" rx="4.5" fill="#d08b4a" />
          <path
            d="M14 77 H44 M52 79 H74"
            stroke="#e8ad6d"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
          <circle cx="106" cy="78" r="2.2" fill="#9a5b2a" />

          {/* Cut slices: each appears at its chop and tips onto the board */}
          <g transform="translate(-8 0)">
            <g
              style={{
                animation: 'meatSlice0 2700ms ease-out infinite',
                transformOrigin: '80px 73px',
                opacity: 0,
              }}
            >
              <rect x="71" y="48" width="9" height="25" rx="3.5" fill="#7f1d1d" />
              <rect x="72.5" y="49.5" width="6" height="22" rx="2.5" fill="#ef4444" />
              <rect x="72.5" y="49.5" width="6" height="4" rx="2" fill="#fde7c8" />
              <path d="M74 58 Q76 61 75 65" stroke="#fecaca" strokeWidth="1.1" fill="none" />
            </g>
          </g>
          <g transform="translate(-17 0)">
            <g
              style={{
                animation: 'meatSlice1 2700ms ease-out infinite',
                transformOrigin: '80px 73px',
                opacity: 0,
              }}
            >
              <rect x="71" y="48" width="9" height="25" rx="3.5" fill="#7f1d1d" />
              <rect x="72.5" y="49.5" width="6" height="22" rx="2.5" fill="#ef4444" />
              <rect x="72.5" y="49.5" width="6" height="4" rx="2" fill="#fde7c8" />
              <path d="M74 58 Q76 61 75 65" stroke="#fecaca" strokeWidth="1.1" fill="none" />
            </g>
          </g>
          <g transform="translate(-26 0)">
            <g
              style={{
                animation: 'meatSlice2 2700ms ease-out infinite',
                transformOrigin: '80px 73px',
                opacity: 0,
              }}
            >
              <rect x="71" y="48" width="9" height="25" rx="3.5" fill="#7f1d1d" />
              <rect x="72.5" y="49.5" width="6" height="22" rx="2.5" fill="#ef4444" />
              <rect x="72.5" y="49.5" width="6" height="4" rx="2" fill="#fde7c8" />
              <path d="M74 58 Q76 61 75 65" stroke="#fecaca" strokeWidth="1.1" fill="none" />
            </g>
          </g>

          {/* Meat slab: shortens with each chop */}
          <g
            style={{
              animation: 'meatSlab 2700ms steps(1, end) infinite',
              transformOrigin: '12px 60px',
            }}
          >
            <path d="M22 47 H72 V73 H22 Q12 73 12 63 V57 Q12 47 22 47 Z" fill="#7f1d1d" />
            <path
              d="M22 49.5 H70 V70.5 H22 Q14.5 70.5 14.5 63 V57 Q14.5 49.5 22 49.5 Z"
              fill="#c81e1e"
            />
            {/* Fat cap */}
            <path
              d="M17 53 Q19 50.5 24 50.5 H69"
              stroke="#fde7c8"
              strokeWidth="3"
              strokeLinecap="round"
              fill="none"
            />
            {/* Marbling */}
            <path
              d="M22 60 Q30 57 36 61 T52 60 M30 66 Q38 63 46 66 T62 65"
              stroke="#f87171"
              strokeWidth="1.3"
              strokeLinecap="round"
              fill="none"
            />
            {/* Fresh cut face */}
            <rect x="66" y="49.5" width="4" height="21" rx="1.5" fill="#ef4444" />
            {/* Gloss */}
            <path
              d="M20 56 Q19 61 21 66"
              stroke="#fff"
              strokeWidth="1.6"
              strokeLinecap="round"
              fill="none"
              opacity="0.45"
            />
          </g>
        </g>

        {/* Impact marks where the edge meets the board */}
        <g
          style={{
            animation: 'meatImpact 2700ms ease-out infinite',
            transformOrigin: '70px 72px',
            opacity: 0,
          }}
          stroke="#7f1d1d"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M61 65 L55 60" />
          <path d="M60 71 L53 71" />
          <path d="M99 65 L105 60" />
        </g>

        {/* Cleaver, pivoting from the end of its handle */}
        <g
          style={{
            animation: 'meatChop 2700ms cubic-bezier(0.55, 0, 0.6, 1) infinite',
            transformOrigin: '114px 50px',
          }}
        >
          {/* Blade */}
          <path d="M66 43 H94 V73 H70 Q66 73 66 69 Z" fill="#475569" />
          <path d="M68 45 H92 V71 H70.5 Q68 71 68 68.5 Z" fill="#cbd5e1" />
          {/* Spine */}
          <rect x="68" y="45" width="24" height="4" fill="#94a3b8" />
          {/* Polished edge */}
          <path d="M68 65 H92 V71 H70.5 Q68 71 68 68.5 Z" fill="#f1f5f9" />
          {/* Hanging hole */}
          <circle cx="73.5" cy="53" r="2.4" fill="#fff" stroke="#475569" strokeWidth="1" />
          {/* Sheen */}
          <path
            d="M78 52 L86 52 M76 58 L88 58"
            stroke="#fff"
            strokeWidth="1.4"
            strokeLinecap="round"
            opacity="0.7"
          />
          {/* Bolster */}
          <rect x="92" y="45" width="5" height="10" rx="1" fill="#64748b" />
          {/* Handle */}
          <rect x="96" y="45.5" width="20" height="9" rx="4.5" fill="#451a03" />
          <rect x="97" y="46.5" width="18" height="7" rx="3.5" fill="#7c2d12" />
          <circle cx="102" cy="50" r="1.3" fill="#fcd34d" />
          <circle cx="109" cy="50" r="1.3" fill="#fcd34d" />
        </g>
      </svg>
    ),
  },
  // Premium cloche: serving dome on a white plate whose lid lifts to reveal a
  // warm glow + steam, circled by a gradient arc with a gold dot at its tip.
  // Uses the title red (--color-primary-dark) and the yellow highlight, never
  // the main brand colour, which is near-black for some merchants.
  restaurant: {
    color: 'var(--color-primary-dark, #b02d29)',
    keyframes: `
      @keyframes rxLoaderSpin { to { transform: rotate(360deg); } }
      @keyframes rxLoaderLid {
        0%, 55%, 100% { transform: translateY(0) rotate(0deg); }
        70% { transform: translateY(-3.5px) rotate(-6deg); }
        82% { transform: translateY(-2px) rotate(-3deg); }
      }
      @keyframes rxLoaderGlow {
        0%, 55%, 100% { opacity: 0; }
        70%, 82% { opacity: 0.9; }
      }
      @keyframes rxLoaderSteam {
        0% { transform: translateY(2px); opacity: 0; }
        40% { opacity: 0.85; }
        100% { transform: translateY(-7px); opacity: 0; }
      }
      @keyframes rxLoaderBreathe {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.03); }
      }
      @media (prefers-reduced-motion: reduce) {
        .rx-loader * { animation: none !important; }
      }
    `,
    icon: (id) => (
      <svg
        className="rx-loader"
        viewBox="0 0 80 80"
        style={{ width: '100%', height: '100%', display: 'block', overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={`${id}-arc`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" style={{ stopColor: 'var(--rx-highlight, #fbbf24)' }} />
            <stop offset="100%" style={{ stopColor: 'var(--color-primary-dark, #b02d29)' }} />
          </linearGradient>
          <radialGradient id={`${id}-glow`} cx="50%" cy="100%" r="60%">
            <stop offset="0%" stopColor="#fde68a" />
            <stop offset="100%" stopColor="#fde68a" stopOpacity="0" />
          </radialGradient>
          <filter id={`${id}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#44260c" floodOpacity="0.14" />
          </filter>
        </defs>

        {/* track + spinning gradient arc with a gold dot at its tip */}
        <circle
          cx="40"
          cy="40"
          r="35"
          fill="none"
          strokeWidth="2.5"
          style={{
            stroke: 'color-mix(in srgb, var(--color-primary-dark, #b02d29) 12%, transparent)',
          }}
        />
        <g style={{ transformOrigin: '40px 40px', animation: 'rxLoaderSpin 1.6s linear infinite' }}>
          <circle
            cx="40"
            cy="40"
            r="35"
            fill="none"
            stroke={`url(#${id}-arc)`}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="70 150"
          />
          <circle cx="75" cy="40" r="3.2" style={{ fill: 'var(--rx-highlight, #fbbf24)' }} />
        </g>

        {/* plate */}
        <g
          style={{
            transformOrigin: '40px 40px',
            animation: 'rxLoaderBreathe 2.4s ease-in-out infinite',
          }}
        >
          <circle cx="40" cy="40" r="27" fill="#fff" filter={`url(#${id}-shadow)`} />

          {/* steam */}
          {[33, 40, 47].map((x, i) => (
            <path
              key={x}
              d={`M${x} 27c-2-2 2-3.6 0-5.6`}
              fill="none"
              strokeWidth="1.8"
              strokeLinecap="round"
              style={{
                stroke: 'color-mix(in srgb, var(--color-primary-dark, #b02d29) 45%, #fff)',
                animation: `rxLoaderSteam 1.6s ease-out ${i * 220}ms infinite`,
              }}
            />
          ))}

          {/* warm glow revealed when the lid lifts */}
          <ellipse
            cx="40"
            cy="47"
            rx="13"
            ry="5"
            fill={`url(#${id}-glow)`}
            style={{ animation: 'rxLoaderGlow 2.4s ease-in-out infinite' }}
          />

          {/* tray */}
          <path
            d="M24 49h32"
            strokeWidth="3"
            strokeLinecap="round"
            style={{
              stroke: 'color-mix(in srgb, var(--color-primary-dark, #b02d29) 75%, #fbbf24)',
            }}
          />

          {/* lid */}
          <g
            style={{
              transformOrigin: '27px 47px',
              animation: 'rxLoaderLid 2.4s ease-in-out infinite',
            }}
          >
            <path
              d="M27 46.5c0-7.6 5.8-13.5 13-13.5s13 5.9 13 13.5z"
              style={{ fill: 'var(--color-primary-dark, #b02d29)' }}
            />
            <path
              d="M31.5 42c.8-3.6 3.6-6.2 7-6.9"
              fill="none"
              stroke="#fff"
              strokeOpacity="0.55"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <circle cx="40" cy="31.2" r="2.4" style={{ fill: 'var(--rx-highlight, #fbbf24)' }} />
          </g>
        </g>
      </svg>
    ),
  },
  pet: {
    color: '#B88458',
    keyframes: `
      @keyframes petRingSpin {
        to { transform: rotate(360deg); }
      }
      @keyframes petGlowPulse {
        0%, 100% { opacity: 0.35; transform: scale(0.92); }
        50% { opacity: 0.75; transform: scale(1.05); }
      }
      @keyframes petPawPulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
    `,
    icon: (gradientId) => (
      <svg viewBox="0 0 72 72" style={{ width: '100%', height: '100%', display: 'block' }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#9D6E48" stopOpacity="0" />
            <stop offset="65%" stopColor="#B88458" />
            <stop offset="100%" stopColor="#C99D72" />
          </linearGradient>
        </defs>
        <circle cx="36" cy="36" r="28" fill="none" stroke="#F8EEE5" strokeWidth="4" opacity="0.5" />
        <circle
          cx="36"
          cy="36"
          r="30"
          fill="none"
          stroke="#B88458"
          strokeWidth="2"
          style={{
            transformOrigin: '36px 36px',
            animation: 'petGlowPulse 1.8s ease-in-out infinite',
          }}
        />
        <circle
          cx="36"
          cy="36"
          r="28"
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray="120 176"
          style={{
            transformOrigin: '36px 36px',
            animation: 'petRingSpin 1.1s linear infinite',
            filter: 'drop-shadow(0 0 4px rgba(157, 110, 72, 0.45))',
          }}
        />
        <g
          style={{
            transformOrigin: '36px 36px',
            animation: 'petPawPulse 1.8s ease-in-out infinite',
          }}
        >
          <ellipse cx="36" cy="40" rx="9" ry="7" fill="#9D6E48" />
          <ellipse cx="24" cy="28" rx="4" ry="5" fill="#C99D72" />
          <ellipse cx="31" cy="21" rx="4.2" ry="5.2" fill="#B88458" />
          <ellipse cx="41" cy="21" rx="4.2" ry="5.2" fill="#B88458" />
          <ellipse cx="48" cy="28" rx="4" ry="5" fill="#C99D72" />
        </g>
      </svg>
    ),
  },
  coffee: {
    color: 'var(--color-badge-bg, #cc9d59)',
    keyframes: `
      @keyframes coffeeArcSweep {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes coffeeArcBreathe {
        0%, 100% { stroke-dasharray: 18 170; }
        50% { stroke-dasharray: 70 118; }
      }
      @keyframes coffeeFillRise {
        0% { transform: translateY(18px); }
        55%, 80% { transform: translateY(0); }
        100% { transform: translateY(18px); }
      }
      @keyframes coffeeSteamDrift {
        0% { transform: translateY(4px); opacity: 0; }
        35% { opacity: 0.85; }
        100% { transform: translateY(-7px); opacity: 0; }
      }
    `,
    icon: (gradientId) => (
      <svg
        viewBox="0 0 72 72"
        style={{ width: '100%', height: '100%', display: 'block', overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={`${gradientId}-arc`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f3d9a4" />
            <stop offset="100%" stopColor="var(--color-badge-bg, #cc9d59)" />
          </linearGradient>
          <linearGradient id={`${gradientId}-brew`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#8a5a33" />
            <stop offset="100%" stopColor="#3b2416" />
          </linearGradient>
          <clipPath id={`${gradientId}-cup`}>
            <path d="M24 30h22v9c0 7-5 11-11 11s-11-4-11-11z" />
          </clipPath>
        </defs>

        {/* Track + sweeping gold arc */}
        <circle
          cx="36"
          cy="36"
          r="30"
          fill="none"
          stroke="var(--color-badge-bg, #cc9d59)"
          strokeWidth="1.25"
          opacity="0.2"
        />
        <circle
          cx="36"
          cy="36"
          r="30"
          fill="none"
          stroke={`url(#${gradientId}-arc)`}
          strokeWidth="2"
          strokeLinecap="round"
          style={{
            transformOrigin: '36px 36px',
            animation:
              'coffeeArcSweep 1.6s cubic-bezier(0.55, 0.15, 0.45, 0.85) infinite, coffeeArcBreathe 1.6s ease-in-out infinite',
          }}
        />

        {/* Steam */}
        <g fill="none" stroke="#f6ecdc" strokeWidth="1.6" strokeLinecap="round">
          <path
            d="M31.5 25c-2-2.5 2-4.5 0-7"
            style={{ animation: 'coffeeSteamDrift 1.8s ease-in-out infinite' }}
          />
          <path
            d="M38.5 25c-2-2.5 2-4.5 0-7"
            style={{ animation: 'coffeeSteamDrift 1.8s ease-in-out 600ms infinite' }}
          />
        </g>

        {/* Espresso filling the cup, with a crema line on top */}
        <g clipPath={`url(#${gradientId}-cup)`}>
          <rect x="22" y="30" width="26" height="22" fill="#f6ecdc" opacity="0.15" />
          <g style={{ animation: 'coffeeFillRise 2.4s ease-in-out infinite' }}>
            <rect x="22" y="32" width="26" height="22" fill={`url(#${gradientId}-brew)`} />
            <rect x="22" y="32" width="26" height="1.6" fill="#d9a86a" />
          </g>
        </g>

        {/* Cup outline, handle, saucer */}
        <g
          fill="none"
          stroke="var(--color-badge-bg, #cc9d59)"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M24 30h22v9c0 7-5 11-11 11s-11-4-11-11z" />
          <path d="M46 33.5c4.5 0 4.5 7.5 0 7.5" />
          <path d="M20 53.5h30" />
        </g>
      </svg>
    ),
  },
  default: {
    color: '#1B5E20',
    keyframes: `
      @keyframes defaultRingSpin {
        to { transform: rotate(360deg); }
      }
      @keyframes defaultRingSpinReverse {
        to { transform: rotate(-360deg); }
      }
      @keyframes defaultGlowPulse {
        0%, 100% { opacity: 0.35; transform: scale(0.94); }
        50% { opacity: 0.65; transform: scale(1.04); }
      }
      @keyframes defaultCartBob {
        0%, 100% { transform: translateY(0) rotate(0deg); }
        50% { transform: translateY(-0.22rem) rotate(-1.2deg); }
      }
      @keyframes defaultWheelSpin {
        to { transform: rotate(360deg); }
      }
      @keyframes defaultShadowPulse {
        0%, 100% { transform: scaleX(1); opacity: 0.16; }
        50% { transform: scaleX(0.8); opacity: 0.26; }
      }
      @keyframes defaultOrbitDot {
        0% { opacity: 0; }
        15%, 85% { opacity: 1; }
        100% { opacity: 0; }
      }
      @keyframes defaultGleam {
        0%, 100% { opacity: 0; }
        50% { opacity: 0.9; }
      }
    `,
    icon: (gradientId) => (
      <svg
        viewBox="0 0 72 72"
        style={{ width: '100%', height: '100%', display: 'block', overflow: 'visible' }}
      >
        <defs>
          <radialGradient id={`${gradientId}-glow`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#A5D6A7" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#A5D6A7" stopOpacity="0" />
          </radialGradient>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#A5D6A7" />
            <stop offset="50%" stopColor="#2E7D32" />
            <stop offset="100%" stopColor="#1B5E20" />
          </linearGradient>
          <linearGradient id={`${gradientId}-ring`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#A5D6A7" stopOpacity="0" />
            <stop offset="100%" stopColor="#1B5E20" />
          </linearGradient>
        </defs>

        <circle
          cx="36"
          cy="36"
          r="30"
          fill={`url(#${gradientId}-glow)`}
          style={{
            transformOrigin: '36px 36px',
            animation: 'defaultGlowPulse 1.6s ease-in-out infinite',
          }}
        />

        <circle
          cx="36"
          cy="36"
          r="27"
          fill="none"
          stroke="var(--color-border, #e5e7eb)"
          strokeWidth="2.5"
          opacity="0.35"
        />

        <circle
          cx="36"
          cy="36"
          r="27"
          fill="none"
          stroke={`url(#${gradientId}-ring)`}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray="56 170"
          style={{
            transformOrigin: '36px 36px',
            animation: 'defaultRingSpin 1.3s linear infinite',
          }}
        />
        <circle
          cx="36"
          cy="36"
          r="22"
          fill="none"
          stroke={`url(#${gradientId}-ring)`}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray="18 120"
          opacity="0.55"
          style={{
            transformOrigin: '36px 36px',
            animation: 'defaultRingSpinReverse 1.8s linear infinite',
          }}
        />

        <ellipse
          cx="36"
          cy="55"
          rx="12"
          ry="2.4"
          fill="#1B5E20"
          style={{
            transformOrigin: '36px 55px',
            animation: 'defaultShadowPulse 1.1s ease-in-out infinite',
          }}
        />

        <g
          style={{
            transformOrigin: '36px 36px',
            animation: 'defaultCartBob 1.3s ease-in-out infinite',
          }}
        >
          <path
            d="M22 20h4.5l4 20h20l4.5-13H29.5"
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth="3.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="drop-shadow(0 2px 3px rgba(27, 94, 32, 0.35))"
          />
          <rect
            x="30"
            y="24"
            width="20.5"
            height="10.5"
            rx="2"
            fill={`url(#${gradientId})`}
            opacity="0.2"
          />
          <path
            d="M35 26.5v6M40.5 26.5v6M46 26.5v6"
            stroke={`url(#${gradientId})`}
            strokeWidth="1.6"
            strokeLinecap="round"
            opacity="0.55"
          />
          <path
            d="M27 21.5c3-3 16-3 19 0"
            fill="none"
            stroke="#F6F3E9"
            strokeWidth="1.6"
            strokeLinecap="round"
            style={{ animation: 'defaultGleam 1.3s ease-in-out infinite' }}
          />

          <g
            style={{
              transformOrigin: '32.5px 48px',
              animation: 'defaultWheelSpin 600ms linear infinite',
            }}
          >
            <circle cx="32.5" cy="48" r="3.6" fill={`url(#${gradientId})`} />
            <circle cx="32.5" cy="48" r="1.1" fill="#fff" />
          </g>
          <g
            style={{
              transformOrigin: '46px 48px',
              animation: 'defaultWheelSpin 600ms linear infinite',
            }}
          >
            <circle cx="46" cy="48" r="3.6" fill={`url(#${gradientId})`} />
            <circle cx="46" cy="48" r="1.1" fill="#fff" />
          </g>
        </g>

        <circle
          cx="10"
          cy="36"
          r="2"
          fill="#2E7D32"
          style={{ animation: 'defaultOrbitDot 1.3s ease-in-out infinite' }}
        />
        <circle
          cx="62"
          cy="36"
          r="2"
          fill="#A5D6A7"
          style={{ animation: 'defaultOrbitDot 1.3s ease-in-out 650ms infinite' }}
        />
      </svg>
    ),
  },
};
